/** Peer-to-peer adoption listing shapes. Mirrors backend adoption-listing serializers. */
import type { Checkout } from '@/features/booking/types';

export type AdoptionSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';
export type AdoptionGender = 'MALE' | 'FEMALE' | 'UNKNOWN';
export type AdoptionAgeUnit = 'WEEKS' | 'MONTHS' | 'YEARS';
export type AdoptionListingStatus = 'DRAFT' | 'PUBLISHED' | 'ADOPTED' | 'CLOSED' | 'HIDDEN' | 'REMOVED';
export type AdoptionRequestStatus = 'PENDING_PAYMENT' | 'CONFIRMED' | 'CANCELLED';

export type AdoptionPhoto = { id: string; is_primary: boolean; file_path: string };

export type AdoptionContact = {
  contact_name: string;
  contact_phone: string;
  address_line: string | null;
  pincode: string | null;
};

export type AdoptionListing = {
  id: string;
  posted_by_user_id: string;
  species: AdoptionSpecies;
  breed: string | null;
  age_value: number | null;
  age_unit: AdoptionAgeUnit | null;
  gender: AdoptionGender;
  vaccinated: boolean | null;
  sterilized: boolean | null;
  health_status: string | null;
  good_with: string | null;
  description: string | null;
  city: string;
  state: string | null;
  status: AdoptionListingStatus;
  phone_verified: boolean;
  published_at: string | null;
  adopted_at: string | null;
  created_at: string;
  photos: AdoptionPhoto[];
  contact: AdoptionContact | null;
  distance_km?: number;
};

export type AdoptionListingPayload = {
  species: AdoptionSpecies;
  contact_name: string;
  contact_phone: string;
  city: string;
  breed?: string;
  age_value?: number;
  age_unit?: AdoptionAgeUnit;
  gender?: AdoptionGender;
  vaccinated?: boolean;
  sterilized?: boolean;
  health_status?: string;
  good_with?: string;
  description?: string;
  address_line?: string;
  state?: string;
  pincode?: string;
  latitude?: number;
  longitude?: number;
};

export type BrowseResponse = {
  items: AdoptionListing[];
  total: number;
  page: number;
  page_size: number;
};

export type AdoptionRequestPayment = { status: string; is_dummy: boolean; order_ref: string | null };

export type AdoptionRequest = {
  id: string;
  listing_id: string;
  status: AdoptionRequestStatus;
  created_at: string;
  payment: AdoptionRequestPayment | null;
};

/** create-request response also carries the razorpay checkout (null for dummy). */
export type CreateAdoptionRequestResponse = AdoptionRequest & { checkout: Checkout | null };

/** my-adoptions rows embed the listing (contact revealed once CONFIRMED). */
export type MyAdoption = AdoptionRequest & { listing: AdoptionListing };

export type InterestedAdopter = {
  id: string;
  status: AdoptionRequestStatus;
  created_at: string;
  adopter: { name: string | null; phone: string | null };
};

export type SendOtpResponse = {
  sent: boolean;
  contact_phone: string;
  expires_in_seconds: number;
  debug_otp_code?: string;
};

export const SPECIES_OPTIONS: Array<{ value: AdoptionSpecies; label: string }> = [
  { value: 'DOG', label: 'Dog' },
  { value: 'CAT', label: 'Cat' },
  { value: 'BIRD', label: 'Bird' },
  { value: 'RABBIT', label: 'Rabbit' },
  { value: 'FISH', label: 'Fish' },
  { value: 'OTHER', label: 'Other' },
];

export const GENDER_OPTIONS: Array<{ value: AdoptionGender; label: string }> = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];

export const AGE_UNIT_OPTIONS: Array<{ value: AdoptionAgeUnit; label: string }> = [
  { value: 'WEEKS', label: 'weeks' },
  { value: 'MONTHS', label: 'months' },
  { value: 'YEARS', label: 'years' },
];

export const SPECIES_LABEL: Record<AdoptionSpecies, string> = Object.fromEntries(
  SPECIES_OPTIONS.map((o) => [o.value, o.label]),
) as Record<AdoptionSpecies, string>;

export function age_label(l: Pick<AdoptionListing, 'age_value' | 'age_unit'>): string | null {
  if (l.age_value == null || !l.age_unit) return null;
  const unit = l.age_value === 1 ? l.age_unit.toLowerCase().replace(/s$/, '') : l.age_unit.toLowerCase();
  return `${l.age_value} ${unit}`;
}

export const ADOPTION_FEE_INR = 149;
